<template>
  <div class="relative inline-flex">
    <button
      type="button"
      class="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-background-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      :title="currentNavLabel"
      @click="openMenu"
    >
      <i class="fa-solid fa-bars text-lg text-secondary"></i>
      <span class="hidden text-sm font-medium sm:inline">Menu</span>
    </button>

    <TransitionRoot :show="isOpen" as="template">
      <Dialog as="div" class="relative z-50" @close="closeMenu">
        <TransitionChild
          as="template"
          enter="transition-opacity duration-200 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity duration-150 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-slate-950/35 backdrop-blur-[2px]"></div>
        </TransitionChild>

        <div class="fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div class="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
              <TransitionChild
                as="template"
                enter="transform transition duration-200 ease-out"
                enter-from="-translate-x-full opacity-0"
                enter-to="translate-x-0 opacity-100"
                leave="transform transition duration-150 ease-in"
                leave-from="translate-x-0 opacity-100"
                leave-to="-translate-x-full opacity-0"
              >
                <DialogPanel
                  class="pointer-events-auto flex h-full w-screen max-w-full flex-col overflow-hidden bg-background-card text-text shadow-2xl ring-1 ring-border sm:ml-1.5 sm:mt-1.5 sm:h-[calc(100vh-0.75rem)] sm:max-w-152 sm:rounded-3xl"
                >
                  <div class="border-b border-border px-3 pb-2.5 pt-3">
                    <div class="flex items-start justify-between gap-1.5">
                      <div class="flex items-start gap-2">
                        <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <img src="/adam-cloud.png" alt="ADAM Cloud Logo" class="h-4 w-4" />
                        </div>
                        <div>
                          <DialogTitle as="h2" class="text-sm font-semibold text-text sm:text-base">
                            Navigate ADAM Cloud
                          </DialogTitle>
                          <p class="mt-0.5 text-[0.6875rem] text-text-muted sm:text-xs">
                            {{
                              currentProject?.name
                                ? `Current project: ${currentProject.name}`
                                : 'Select a project to unlock project-specific pages.'
                            }}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        class="inline-flex h-8 w-8 items-center justify-center rounded-full text-secondary transition-colors hover:bg-background-hover hover:text-text focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        @click="closeMenu"
                      >
                        <i class="fa-solid fa-xmark text-sm"></i>
                      </button>
                    </div>

                    <div class="mt-1.5 flex flex-wrap items-center gap-1 text-[0.6875rem]">
                      <span
                        v-if="isSystemAdmin"
                        class="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary"
                      >
                        Administrator access
                      </span>
                      <span class="rounded-full bg-background-hover px-2 py-0.5 font-medium text-text-muted">
                        v{{ appVersion }}
                      </span>
                    </div>
                  </div>

                  <div class="border-b border-border px-3 py-2">
                    <label
                      for="navigation-search"
                      class="mb-1 block text-[0.6875rem] font-semibold uppercase tracking-wide text-secondary"
                    >
                      Quick search
                    </label>
                    <div class="relative">
                      <i
                        class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-secondary"
                      ></i>
                      <input
                        id="navigation-search"
                        ref="searchInput"
                        v-model="searchQuery"
                        type="search"
                        class="w-full rounded-lg border border-border bg-background px-8 py-2 text-[0.8125rem] text-text outline-hidden transition-colors placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
                        placeholder="Search pages, reports, or materials..."
                      />
                      <button
                        v-if="hasSearchQuery"
                        type="button"
                        class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-1 py-0.5 text-[0.625rem] font-medium text-secondary transition-colors hover:bg-background-hover hover:text-text"
                        @click="clearSearch"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div class="flex-1 overflow-y-auto px-3 py-2">
                    <div class="space-y-2">
                      <button
                        type="button"
                        :class="[
                          'flex w-full items-start justify-between gap-2 rounded-lg border bg-background px-2.5 py-2 text-left transition-colors hover:bg-background-hover',
                          hasSelectedProject
                            ? 'border-primary/20 hover:border-primary/40'
                            : 'border-dashed border-border hover:border-primary/40'
                        ]"
                        @click="goToProjectSelection"
                      >
                        <div class="flex items-start gap-2">
                          <div
                            :class="[
                              'flex h-7 w-7 items-center justify-center rounded-lg',
                              hasSelectedProject ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
                            ]"
                          >
                            <i class="fa-solid fa-folder-open"></i>
                          </div>
                          <div>
                            <div class="text-xs font-semibold text-text">
                              {{ hasSelectedProject ? 'Switch project' : 'Select a project' }}
                            </div>
                            <p class="mt-0.5 text-[0.6875rem] text-text-muted">
                              {{
                                currentProject?.name
                                  ? `Current project: ${currentProject.name}. Open the project selection page to change context.`
                                  : hasSelectedProject
                                    ? 'Open the project selection page to change the current project context.'
                                    : 'Choose a project before opening project-scoped pages and search results.'
                              }}
                            </p>
                          </div>
                        </div>
                        <i class="fa-solid fa-arrow-right text-sm text-secondary"></i>
                      </button>

                      <section v-if="!hasSearchQuery && recentItems.length">
                        <div class="mb-1.5 flex items-center justify-between">
                          <div>
                            <h3 class="text-[0.6875rem] font-semibold uppercase tracking-wide text-secondary">Recent</h3>
                            <p class="mt-0.5 text-[0.625rem] text-text-muted">
                              Your last three visible navigation destinations.
                            </p>
                          </div>
                        </div>

                        <div class="grid gap-1 sm:grid-cols-3">
                          <button
                            v-for="item in recentItems"
                            :key="item.id"
                            type="button"
                            :class="[
                              'rounded-lg border px-2 py-1.5 text-left transition-colors',
                              isActive(item)
                                ? 'border-primary bg-primary text-white'
                                : 'border-border bg-background hover:bg-background-hover text-text'
                            ]"
                            @click="handleNavigate(item)"
                          >
                            <div class="flex items-center gap-1">
                              <i :class="['fa-solid', item.icon, isActive(item) ? 'text-white' : 'text-primary']"></i>
                              <span class="truncate text-xs font-medium">{{ item.label }}</span>
                            </div>
                            <div
                              class="mt-0.5 text-[0.625rem]"
                              :class="isActive(item) ? 'text-white/80' : 'text-text-muted'"
                            >
                              {{ getCategoryLabel(item) }}
                            </div>
                          </button>
                        </div>
                      </section>

                      <div
                        v-if="displayedTopStandaloneItems.length || displayedGroups.length || displayedBottomStandaloneItems.length"
                        class="space-y-2"
                      >
                        <div v-if="displayedTopStandaloneItems.length" class="grid gap-1.5 sm:grid-cols-2">
                          <button
                            v-for="item in displayedTopStandaloneItems"
                            :key="item.id"
                            type="button"
                            :disabled="isActive(item)"
                            :class="[
                              'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors',
                              isActive(item)
                                ? 'cursor-default border-primary bg-primary text-white'
                                : 'border-border bg-background hover:border-primary/30 hover:bg-background-hover text-text'
                            ]"
                            @click="handleNavigate(item)"
                          >
                            <div
                              :class="[
                                'mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg',
                                isActive(item)
                                  ? 'bg-white/15 text-white'
                                  : 'bg-primary/10 text-primary'
                              ]"
                            >
                              <i :class="['fa-solid', item.icon]"></i>
                            </div>

                            <div class="min-w-0 flex-1">
                              <div class="flex items-center gap-1">
                                <span class="truncate text-sm font-semibold">{{ item.label }}</span>
                                <span
                                  v-if="isActive(item)"
                                  class="rounded-full bg-white/15 px-1.5 py-0.5 text-[0.625rem] font-medium text-white"
                                >
                                  Current
                                </span>
                              </div>
                              <p
                                class="mt-0.5 wrap-anywhere text-[0.6875rem] leading-normal"
                                :class="isActive(item) ? 'text-white/80' : 'text-text-muted'"
                              >
                                {{ item.description }}
                              </p>
                            </div>

                            <i
                              class="fa-solid fa-arrow-up-right-from-square mt-1 text-xs"
                              :class="isActive(item) ? 'text-white/70' : 'text-secondary'"
                            ></i>
                          </button>
                        </div>

                        <div v-if="displayedGroups.length" class="grid gap-1.5 md:grid-cols-2">
                          <section
                            v-for="group in displayedGroups"
                            :key="group.category.id"
                            class="rounded-lg border border-border bg-background/70 p-2"
                          >
                            <div class="mb-1.5">
                              <div class="flex items-center gap-1 text-text">
                                <i :class="['fa-solid', group.category.icon, 'text-primary']"></i>
                                <h3 class="text-xs font-semibold">{{ group.category.label }}</h3>
                              </div>
                              <p class="mt-0.5 text-[0.625rem] text-text-muted">{{ group.category.description }}</p>
                            </div>

                            <div class="space-y-1">
                              <button
                                v-for="item in group.items"
                                :key="item.id"
                                type="button"
                                :disabled="isActive(item)"
                                :class="[
                                  'flex w-full items-start gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors',
                                  isActive(item)
                                    ? 'cursor-default border-primary bg-primary text-white'
                                    : 'border-transparent bg-transparent hover:border-border hover:bg-background-hover text-text'
                                ]"
                                @click="handleNavigate(item)"
                              >
                                <div
                                  :class="[
                                    'mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg',
                                    isActive(item)
                                      ? 'bg-white/15 text-white'
                                      : 'bg-primary/10 text-primary'
                                  ]"
                                >
                                  <i :class="['fa-solid', item.icon]"></i>
                                </div>

                                <div class="min-w-0 flex-1">
                                  <div class="flex items-center gap-1">
                                    <span class="truncate text-xs font-medium">{{ item.label }}</span>
                                    <span
                                      v-if="isActive(item)"
                                      class="rounded-full bg-white/15 px-1.5 py-0.5 text-[0.625rem] font-medium text-white"
                                    >
                                      Current
                                    </span>
                                  </div>
                                  <p
                                    class="mt-0.5 wrap-anywhere text-[0.625rem] leading-normal"
                                    :class="isActive(item) ? 'text-white/80' : 'text-text-muted'"
                                  >
                                    {{ item.description }}
                                  </p>
                                </div>

                                <i
                                  class="fa-solid fa-arrow-up-right-from-square mt-1 text-xs"
                                  :class="isActive(item) ? 'text-white/70' : 'text-secondary'"
                                ></i>
                              </button>
                            </div>
                          </section>
                        </div>

                        <div v-if="displayedBottomStandaloneItems.length" class="grid gap-1.5">
                          <button
                            v-for="item in displayedBottomStandaloneItems"
                            :key="item.id"
                            type="button"
                            :disabled="isActive(item)"
                            :class="[
                              'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors',
                              isActive(item)
                                ? 'cursor-default border-primary bg-primary text-white'
                                : 'border-border bg-background hover:border-primary/30 hover:bg-background-hover text-text'
                            ]"
                            @click="handleNavigate(item)"
                          >
                            <div
                              :class="[
                                'mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg',
                                isActive(item)
                                  ? 'bg-white/15 text-white'
                                  : 'bg-primary/10 text-primary'
                              ]"
                            >
                              <i :class="['fa-solid', item.icon]"></i>
                            </div>

                            <div class="min-w-0 flex-1">
                              <div class="flex items-center gap-1">
                                <span class="truncate text-sm font-semibold">{{ item.label }}</span>
                                <span
                                  v-if="isActive(item)"
                                  class="rounded-full bg-white/15 px-1.5 py-0.5 text-[0.625rem] font-medium text-white"
                                >
                                  Current
                                </span>
                              </div>
                              <p
                                class="mt-0.5 wrap-anywhere text-[0.6875rem] leading-normal"
                                :class="isActive(item) ? 'text-white/80' : 'text-text-muted'"
                              >
                                {{ item.description }}
                              </p>
                            </div>

                            <i
                              class="fa-solid fa-arrow-up-right-from-square mt-1 text-xs"
                              :class="isActive(item) ? 'text-white/70' : 'text-secondary'"
                            ></i>
                          </button>
                        </div>
                      </div>

                      <div
                        v-else
                        class="rounded-lg border border-dashed border-border bg-background px-3 py-5 text-center"
                      >
                        <div class="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-background-hover text-secondary">
                          <i class="fa-solid fa-magnifying-glass"></i>
                        </div>
                        <h3 class="mt-2 text-sm font-semibold text-text">
                          {{ hasSearchQuery ? 'No results found' : 'Navigation is waiting for context' }}
                        </h3>
                        <p class="mt-1 text-[0.6875rem] text-text-muted">
                          {{
                            hasSearchQuery
                              ? 'Try a different search term or clear the filter.'
                              : isSystemAdmin
                                ? 'Project-specific items are hidden until a project is selected.'
                                : 'Select a project to reveal the rest of the application navigation.'
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import {
  getNavigationCategory,
  type NavigationItem,
} from '@/shared/config/navigation';
import { useNavigationMenu } from '@/shared/composables/useNavigationMenu';
import { useAppInfo } from '@/shared/services/app';

const route = useRoute();
const router = useRouter();
const { appVersion } = useAppInfo();

const {
  currentNavItem,
  currentProject,
  displayedBottomStandaloneItems,
  displayedGroups,
  displayedTopStandaloneItems,
  hasSearchQuery,
  hasSelectedProject,
  isSystemAdmin,
  recentItems,
  searchQuery,
  clearSearch,
} = useNavigationMenu();

const isOpen = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);

const currentNavLabel = computed(() => currentNavItem.value?.label ?? 'Open navigation');

watch(isOpen, async (open) => {
  if (open) {
    await nextTick();
    searchInput.value?.focus();
    return;
  }

  clearSearch();
});

watch(
  () => route.fullPath,
  () => {
    if (isOpen.value) {
      closeMenu();
    }
  },
);

function openMenu(): void {
  isOpen.value = true;
}

function closeMenu(): void {
  isOpen.value = false;
}

function isActive(item: NavigationItem): boolean {
  return currentNavItem.value?.id === item.id;
}

function getCategoryLabel(item: NavigationItem): string {
  if (item.standalone) {
    return 'Quick access';
  }

  return getNavigationCategory(item.category).label;
}

async function handleNavigate(item: NavigationItem): Promise<void> {
  if (isActive(item)) {
    closeMenu();
    return;
  }

  closeMenu();
  await router.push(item.route.name);
}

async function goToProjectSelection(): Promise<void> {
  closeMenu();
  await router.push({ path: '/home/project-selection' });
}
</script>
